import React, { useEffect, useState } from 'react';
import { X, ChevronDown, Info, Check, MapPin, Phone, Shield, Calendar, Send, Clock, User, FileText, MessageSquare, AlertCircle, Loader2 } from 'lucide-react';
import { useToast } from './Toast';
interface ActionDetailPanelProps {
  isOpen: boolean;
  siteName: string;
  onClose: () => void;
}
interface TimelineEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  type: 'system' | 'user' | 'note' | 'status';
}
interface SuggestedGuard {
  id: string;
  name: string;
  currentShifts: number;
  maxShifts: number;
  grade: string;
  distance: string;
  contact: string;
}
// Mock Data
const timelineData: TimelineEntry[] = [{
  id: '3',
  timestamp: '2 Feb, 08:30',
  user: 'Sarah van Wyk',
  action: 'Dispatched reliever: John Mkhize',
  type: 'user'
}, {
  id: '2',
  timestamp: '2 Feb, 07:15',
  user: 'Sarah van Wyk',
  action: "Added note: 'Contacted original guard, confirmed sick'",
  type: 'note'
}, {
  id: '1',
  timestamp: '2 Feb, 06:45',
  user: 'System',
  action: 'Action created: Guard Sipho Dlamini did not arrive at site',
  type: 'system'
}];
const suggestedGuards: SuggestedGuard[] = [{
  id: 'guard-1',
  name: 'John Mkhize',
  currentShifts: 14,
  maxShifts: 19,
  grade: 'Grade B',
  distance: 'Within 5km',
  contact: '082 345 6789'
}, {
  id: 'guard-2',
  name: 'Thabo Ndlovu',
  currentShifts: 12,
  maxShifts: 19,
  grade: 'Grade B',
  distance: 'Within 3km',
  contact: '083 456 7890'
}, {
  id: 'guard-3',
  name: 'Sipho Dlamini',
  currentShifts: 15,
  maxShifts: 19,
  grade: 'Grade A',
  distance: 'Within 7km',
  contact: '084 567 8901'
}];
export function ActionDetailPanel({
  isOpen,
  siteName,
  onClose
}: ActionDetailPanelProps) {
  const {
    showToast
  } = useToast();
  const [status, setStatus] = useState<'Open' | 'In Progress' | 'Resolved'>('In Progress');
  const [selectedGuardId, setSelectedGuardId] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [timeline, setTimeline] = useState<TimelineEntry[]>(timelineData);
  const [isSavingNote, setIsSavingNote] = useState(false);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (isNoteModalOpen && !isSavingNote) {
          setIsNoteModalOpen(false);
          setNoteText('');
        } else if (!isNoteModalOpen) {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isNoteModalOpen, onClose, isSavingNote]);
  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handleSelectGuard = (guardId: string) => {
    setSelectedGuardId(guardId === selectedGuardId ? null : guardId);
  };
  const handleDispatch = () => {
    if (selectedGuardId) {
      const guard = suggestedGuards.find(g => g.id === selectedGuardId);
      console.log('Dispatching guard:', selectedGuardId);
      // Add dispatch entry to timeline
      const now = new Date();
      const timestamp = now.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short'
      }) + ', ' + now.toLocaleTimeString('en-ZA', {
        hour: '2-digit',
        minute: '2-digit'
      });
      const newEntry: TimelineEntry = {
        id: String(Date.now()),
        timestamp,
        user: 'Sarah van Wyk',
        action: `Dispatched reliever: ${guard?.name || 'Unknown'}`,
        type: 'user'
      };
      setTimeline(prev => [newEntry, ...prev]);
      setStatus('Resolved');
      setSelectedGuardId(null);
      showToast('Guard dispatched successfully', 'success');
    }
  };
  const handleSaveNote = async () => {
    if (noteText.trim()) {
      setIsSavingNote(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      const now = new Date();
      const timestamp = now.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short'
      }) + ', ' + now.toLocaleTimeString('en-ZA', {
        hour: '2-digit',
        minute: '2-digit'
      });
      const newEntry: TimelineEntry = {
        id: String(Date.now()),
        timestamp,
        user: 'Sarah van Wyk',
        action: `Added note: '${noteText.trim()}'`,
        type: 'note'
      };
      setTimeline(prev => [newEntry, ...prev]);
      setNoteText('');
      setIsNoteModalOpen(false);
      setIsSavingNote(false);
      showToast('Note added', 'success');
    }
  };
  const getStatusColor = (s: string) => {
    switch (s) {
      case 'Open':
        return 'bg-sky-500 text-white';
      case 'In Progress':
        return 'bg-amber-500 text-white';
      case 'Resolved':
        return 'bg-emerald-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };
  const getTimelineIcon = (type: TimelineEntry['type']) => {
    switch (type) {
      case 'system':
        return <AlertCircle className="h-4 w-4 text-white" data-id="element-2" />;
      case 'note':
        return <MessageSquare className="h-4 w-4 text-white" data-id="element-3" />;
      case 'user':
        return <User className="h-4 w-4 text-white" data-id="element-4" />;
      case 'status':
        return <Check className="h-4 w-4 text-white" data-id="element-5" />;
      default:
        return <div className="h-2 w-2 rounded-full bg-white" data-id="element-6" />;
    }
  };
  const getTimelineIconBg = (type: TimelineEntry['type']) => {
    switch (type) {
      case 'system':
        return 'bg-[#ec003f]';
      // Error/Destructive
      case 'note':
        return 'bg-[#5a7280]';
      // Secondary accent
      case 'user':
        return 'bg-[#062133]';
      // Primary
      case 'status':
        return 'bg-emerald-500';
      // Success
      default:
        return 'bg-gray-400';
    }
  };
  return <>
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-[#062133]/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} aria-hidden="true" data-id="element-7" />

      {/* Panel */}
      <div className={`fixed inset-0 md:inset-y-0 md:right-0 md:left-auto z-50 w-full md:w-[480px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} role="dialog" aria-modal="true" aria-labelledby="detail-panel-title" data-id="element-8">
        {/* Header - Sticky */}
        <div className="flex-none flex items-center justify-between px-6 py-5 border-b border-[#e5e5e5] bg-white z-10" data-id="element-9">
          <div className="flex items-center gap-3" data-id="element-10">
            <h2 id="detail-panel-title" className="text-xl font-bold text-[#062133]" data-id="element-11">
              Action #1042
            </h2>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${getStatusColor(status)}`} data-id="element-12">
              {status}
            </span>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Close panel" data-id="element-13">
            <X className="h-5 w-5" data-id="element-14" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto" data-id="element-15">
          <div className="px-6 py-6 space-y-8" data-id="element-16">
            {/* Summary Section */}
            <div className="grid grid-cols-2 gap-y-4 gap-x-6" data-id="element-17">
              <div data-id="element-18">
                <p className="text-xs font-medium text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-19">
                  Site
                </p>
                <p className="text-sm font-semibold text-[#062133]" data-id="element-20">
                  {siteName}
                </p>
              </div>
              <div data-id="element-21">
                <p className="text-xs font-medium text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-22">
                  Type
                </p>
                <p className="text-sm font-medium text-[#062133]" data-id="element-23">
                  Real-Time Exception
                </p>
              </div>
              <div data-id="element-24">
                <p className="text-xs font-medium text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-25">
                  Priority
                </p>
                <div className="flex items-center gap-1.5" data-id="element-26">
                  <div className="h-2 w-2 rounded-full bg-[#ec003f]" data-id="element-27" />
                  <p className="text-sm font-medium text-[#062133]" data-id="element-28">High</p>
                </div>
              </div>
              <div data-id="element-29">
                <p className="text-xs font-medium text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-30">
                  Created
                </p>
                <p className="text-sm text-[#062133]" data-id="element-31">2 Feb 2026, 06:45</p>
              </div>
              <div className="col-span-2" data-id="element-32">
                <p className="text-xs font-medium text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-33">
                  Assigned To
                </p>
                <div className="flex items-center gap-2" data-id="element-34">
                  <div className="h-6 w-6 rounded-full bg-[#062133] flex items-center justify-center text-white text-xs font-medium" data-id="element-35">
                    SV
                  </div>
                  <p className="text-sm font-medium text-[#062133]" data-id="element-36">
                    Sarah van Wyk
                  </p>
                </div>
              </div>
            </div>

            <div className="h-px bg-[#e5e5e5]" data-id="element-37" />

            {/* Timeline Section */}
            <div data-id="element-38">
              <h3 className="text-sm font-bold text-[#062133] mb-4" data-id="element-39">
                Activity Timeline
              </h3>
              <div className="relative pl-4 space-y-6" data-id="element-40">
                {/* Vertical Line */}
                <div className="absolute left-[23px] top-2 bottom-2 w-0.5 bg-[#e5e5e5]" data-id="element-41" />

                {timeline.map(entry => <div key={entry.id} className="relative flex gap-4" data-id="element-42">
                    {/* Icon/Dot */}
                    <div className={`relative z-10 flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${getTimelineIconBg(entry.type)}`} data-id="element-43">
                      {getTimelineIcon(entry.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pt-1" data-id="element-44">
                      <div className="flex items-center justify-between mb-1" data-id="element-45">
                        <span className="text-xs font-medium text-[#5a7280]" data-id="element-46">
                          {entry.timestamp}
                        </span>
                        <span className="text-xs font-semibold text-[#062133]" data-id="element-47">
                          {entry.user}
                        </span>
                      </div>
                      <p className="text-sm text-[#062133] bg-[#fafaf9] p-3 rounded-lg border border-[#e5e5e5]" data-id="element-48">
                        {entry.action}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Suggested Relievers (if not resolved) */}
            {status !== 'Resolved' && <div className="space-y-4 pt-4 border-t border-[#e5e5e5]" data-id="element-49">
                <div className="flex items-center gap-2" data-id="element-50">
                  <h3 className="text-sm font-bold text-[#062133]" data-id="element-51">
                    Suggested Relievers
                  </h3>
                  <div className="relative" data-id="element-52">
                    <button type="button" className="p-1 text-[#5a7280] hover:text-[#062133] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} onClick={() => setShowTooltip(!showTooltip)} aria-label="More information" data-id="element-53">
                      <Info className="h-4 w-4" data-id="element-54" />
                    </button>
                    {showTooltip && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-[#062133] text-white text-xs rounded-lg whitespace-nowrap z-10 shadow-lg" data-id="element-55">
                        Guards who can cover this shift without incurring
                        overtime
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#062133]" data-id="element-56" />
                      </div>}
                  </div>
                </div>

                <div className="space-y-3" data-id="element-57">
                  {suggestedGuards.map(guard => {
                const isSelected = selectedGuardId === guard.id;
                return <div key={guard.id} className={`p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#ff697f] bg-[#ff697f]/5' : 'border-[#e5e5e5] bg-white hover:border-[#5a7280]/30'}`} data-id="element-58">
                        <div className="flex items-start justify-between gap-3" data-id="element-59">
                          <div className="flex-1 min-w-0" data-id="element-60">
                            <h4 className="font-semibold text-[#062133] truncate" data-id="element-61">
                              {guard.name}
                            </h4>
                            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs" data-id="element-62">
                              <div className="flex items-center gap-1.5 text-[#5a7280]" data-id="element-63">
                                <Calendar className="h-3.5 w-3.5 flex-shrink-0" data-id="element-64" />
                                <span data-id="element-65">
                                  {guard.currentShifts} of {guard.maxShifts}{' '}
                                  shifts
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 text-[#5a7280]" data-id="element-66">
                                <Shield className="h-3.5 w-3.5 flex-shrink-0" data-id="element-67" />
                                <span data-id="element-68">{guard.grade}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-[#5a7280]" data-id="element-69">
                                <MapPin className="h-3.5 w-3.5 flex-shrink-0" data-id="element-70" />
                                <span data-id="element-71">{guard.distance}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-[#5a7280]" data-id="element-72">
                                <Phone className="h-3.5 w-3.5 flex-shrink-0" data-id="element-73" />
                                <span data-id="element-74">{guard.contact}</span>
                              </div>
                            </div>
                          </div>
                          <button type="button" onClick={() => handleSelectGuard(guard.id)} className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-all min-h-[44px] min-w-[80px] flex items-center justify-center ${isSelected ? 'bg-[#ff697f] text-white cursor-default' : 'bg-white border border-[#e5e5e5] text-[#5a7280] hover:border-[#ff697f] hover:text-[#ff697f]'}`} data-id="element-75">
                            {isSelected ? <span className="flex items-center gap-1" data-id="element-76">
                                <Check className="h-3.5 w-3.5" data-id="element-77" />
                                Selected
                              </span> : 'Select'}
                          </button>
                        </div>
                      </div>;
              })}
                </div>

                <button type="button" onClick={handleDispatch} disabled={!selectedGuardId} className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-xl transition-all min-h-[48px] ${selectedGuardId ? 'bg-[#062133] text-white hover:bg-[#0a3249] shadow-sm' : 'bg-[#e5e5e5] text-[#5a7280] cursor-not-allowed'}`} data-id="element-78">
                  <Send className="h-4 w-4" data-id="element-79" />
                  Dispatch Selected
                </button>
              </div>}
          </div>
        </div>

        {/* Footer Actions - Sticky */}
        <div className="flex-none px-6 py-5 border-t border-[#e5e5e5] bg-[#fafaf9] flex gap-3 pb-safe" data-id="element-80">
          <button onClick={() => setIsNoteModalOpen(true)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[#062133] bg-white border border-[#e5e5e5] rounded-xl hover:bg-[#fafaf9] hover:border-[#5a7280] transition-colors min-h-[48px]" data-id="element-81">
            <MessageSquare className="h-4 w-4" data-id="element-82" />
            Add Note
          </button>

          <div className="relative flex-1" data-id="element-83">
            <button onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#062133] rounded-xl hover:bg-[#0a3249] transition-colors min-h-[48px]" data-id="element-84">
              Change Status
              <ChevronDown className="h-4 w-4" data-id="element-85" />
            </button>

            {isStatusDropdownOpen && <>
                <div className="fixed inset-0 z-10" onClick={() => setIsStatusDropdownOpen(false)} data-id="element-86" />
                <div className="absolute bottom-full right-0 mb-2 w-full bg-white border border-[#e5e5e5] rounded-xl shadow-xl overflow-hidden z-20" data-id="element-87">
                  {['Open', 'In Progress', 'Resolved'].map(s => <button key={s} onClick={() => {
                setStatus(s as any);
                setIsStatusDropdownOpen(false);
              }} className="w-full text-left px-4 py-3 text-sm text-[#062133] hover:bg-[#fafaf9] transition-colors flex items-center justify-between min-h-[48px]" data-id="element-88">
                      {s}
                      {status === s && <Check className="h-4 w-4 text-[#ff697f]" data-id="element-89" />}
                    </button>)}
                </div>
              </>}
          </div>
        </div>
      </div>

      {/* Add Note Modal */}
      {isNoteModalOpen && <>
          {/* Modal Backdrop */}
          <div className="fixed inset-0 bg-[#062133]/50 backdrop-blur-sm z-[60] transition-opacity duration-200" onClick={() => {
        if (!isSavingNote) {
          setIsNoteModalOpen(false);
          setNoteText('');
        }
      }} aria-hidden="true" data-id="element-90" />

          {/* Modal */}
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-91">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all" onClick={e => e.stopPropagation()} data-id="element-92">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e5e5]" data-id="element-93">
                <h3 className="text-lg font-bold text-[#062133]" data-id="element-94">Add Note</h3>
                <button onClick={() => {
              if (!isSavingNote) {
                setIsNoteModalOpen(false);
                setNoteText('');
              }
            }} disabled={isSavingNote} className="p-1.5 text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50" aria-label="Close modal" data-id="element-95">
                  <X className="h-5 w-5" data-id="element-96" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-5" data-id="element-97">
                <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Enter your note..." rows={4} disabled={isSavingNote} className="w-full px-4 py-3 bg-white border border-[#e5e5e5] rounded-xl text-[#062133] placeholder-[#5a7280]/60 resize-none hover:border-[#5a7280] focus:outline-none focus:ring-2 focus:ring-[#ff697f] focus:border-transparent transition-colors disabled:bg-[#fafaf9] disabled:cursor-not-allowed" autoFocus data-id="element-98" />
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e5e5e5] bg-[#fafaf9] rounded-b-2xl" data-id="element-99">
                <button onClick={() => {
              setIsNoteModalOpen(false);
              setNoteText('');
            }} disabled={isSavingNote} className="px-4 py-2.5 text-sm font-medium text-[#5a7280] bg-white border border-[#e5e5e5] rounded-xl hover:bg-[#fafaf9] hover:text-[#062133] transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-100">
                  Cancel
                </button>
                <button onClick={handleSaveNote} disabled={!noteText.trim() || isSavingNote} className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-colors min-h-[44px] flex items-center gap-2 ${noteText.trim() && !isSavingNote ? 'text-white bg-[#ff697f] hover:bg-[#e65a6f] shadow-sm' : 'text-[#5a7280] bg-[#e5e5e5] cursor-not-allowed'}`} data-id="element-101">
                  {isSavingNote && <Loader2 className="h-4 w-4 animate-spin" data-id="element-102" />}
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </>}
    </>;
}